/**
 * Dev-only repro for GEA-6191: a popover combobox inside a `ModalNext`, placed
 * where the real one sits — last column of a wide table, last row of the panel —
 * so its content runs past the panel's right edge *and* its bottom edge at once.
 *
 * The real case is `IssuePartsToWorkOrderModal` + `JobCodeCombobox`, which needs
 * an M5-connected org to open. That is why GEA-5837 shipped the portal change
 * without a clipped-case repro and why this bug reached three customer reports.
 * This page is the same geometry with static data and no GraphQL, so anyone can
 * open it on any dev org.
 *
 * Open `/dev/modal-popover-clipping` on the buyers app in dev, then click the
 * "Select job code" cell in the LAST row. Correct: the list opens whole, outside
 * the panel, and scrolls its own 300px of rows. Broken: it is cut off at the
 * panel's edge — which is what `overflow-hidden` on `Dialog.Panel` did.
 *
 * The bottom edge is the axis that matters. `align` can move a popover away from
 * one clipped edge, never off both, so a repro that only overruns the right edge
 * passes while the bug is still shipped.
 */

import Modal from '@/gf/components/ModalNext'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/gf/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/gf/components/ui/popover'
import SelectorTrigger from '@/gf/components/ui/selector-trigger'
import { ModalSize } from '@/types'
import { useState } from 'react'

// Enough rows that the list is taller than its own 300px cap and has to scroll,
// the same as a real work order's task list.
const JOB_CODES = Array.from({ length: 24 }, (_, i) => ({
  taskId: `03-0${String(32 + i).padStart(2, '0')}-002`,
  description: `REPLACE NEW ${['Starter', 'Windshield', 'Battery & Cables', 'Alternator'][i % 4]}`,
}))

// GEA-7577 needs the panel taller than the viewport, which is what puts the
// dialog's own `overflow-y-auto` scroller in play — 25 rows, the size of the
// work order Walter reported (WO 238083, ~25 line items). Four rows fit on
// screen, so the earlier version of this page had no outer scroll to lose and
// could not show that bug at all.
const PART_NAMES = [
  'Starter',
  'LT SI 5011CL 300ML',
  'Diesel Exhaust Fluid Header',
  'Fuel Filter',
  'Alternator',
  'Windshield Wiper Blade',
  'Battery & Cables',
  'Hydraulic Hose',
]

const PARTS = Array.from({ length: 25 }, (_, i) => ({
  mpn: `${30483 + i * 137}N`,
  name: PART_NAMES[i % PART_NAMES.length],
}))

const JobCodePicker = () => {
  const [open, setOpen] = useState(false)
  const [jobCode, setJobCode] = useState<string | undefined>(undefined)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <SelectorTrigger value={jobCode} placeholder="Select job code" />
      </PopoverTrigger>
      <PopoverContent className="p-0" align="end">
        <Command shouldFilter>
          <CommandInput placeholder="Search job codes..." />
          <CommandList>
            <CommandEmpty>No job codes found.</CommandEmpty>
            <CommandGroup>
              {JOB_CODES.map((task) => (
                <CommandItem
                  key={task.taskId}
                  value={task.taskId}
                  keywords={[task.taskId, task.description]}
                  onSelect={() => {
                    setJobCode(task.taskId)
                    setOpen(false)
                  }}
                >
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{task.taskId}</span>
                    <span className="text-gray-700 text-sm">{task.description}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const ModalPopoverClipping = () => {
  const [open, setOpen] = useState(true)

  return (
    <div className="p-8">
      <button type="button" className="text-blue-600 underline" onClick={() => setOpen(true)}>
        Reopen the modal
      </button>

      {open && (
        <Modal onClose={() => setOpen(false)} size={ModalSize.XL}>
          <div className="p-6">
            <h2 className="text-lg text-gray-900">Issue Parts to Work Order</h2>
            <p className="mt-1 text-sm text-gray-500">
              GEA-6191 repro. Open the job code picker in the last row: it must be fully visible,
              including the rows below the panel&apos;s bottom edge.
            </p>

            <table className="mt-6 w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="pb-2 font-normal">Part</th>
                  <th className="pb-2 font-normal">Qty</th>
                  <th className="pb-2 font-normal">Location</th>
                  <th className="pb-2 font-normal w-72">Job code</th>
                </tr>
              </thead>
              <tbody>
                {PARTS.map((part) => (
                  <tr key={part.mpn} className="border-t border-gray-200">
                    <td className="py-3">
                      <div className="font-medium text-gray-900">{part.mpn}</div>
                      <div className="text-gray-500">{part.name}</div>
                    </td>
                    <td className="py-3">1</td>
                    <td className="py-3">ARBRIO</td>
                    <td className="py-3 pl-4">
                      <JobCodePicker />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default ModalPopoverClipping
